import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getCurrentUser } from "@/utils/auth-helpers";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import {
  getOrganizationId,
  supabase,
  switchOrganization,
} from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import CreateOrganizationButton from "./CreateOrganizationButton";

interface Organization {
  id: string;
  name: string;
  role: string;
}

// No longer using local storage for organization state

export default function OrganizationSwitcher() {
  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const { currentOrganization, setCurrentOrganization, loadOrganizations } =
    useOrganization();
  const [isLoading, setIsLoading] = useState(false);
  // Only platform admins create organizations (enforced by create_organization)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    fetchOrganizations();
    supabase
      .rpc("is_platform_admin")
      .then(({ data }) => setIsPlatformAdmin(data === true));
  }, []);

  useEffect(() => {
    // If we already have a current organization set in the context, use that
    if (currentOrganization) {
      return;
    }

    // Otherwise, use organizations list and JWT data to set the current organization
    if (organizations.length > 0 && !currentOrganization) {
      const setActiveOrgFromJwt = async () => {
        try {
          // Get organization ID from JWT token
          const orgId = await getOrganizationId();
          console.log("Got organization ID from JWT:", orgId);

          if (orgId) {
            // Find the organization in our list
            const activeOrg = organizations.find((org) => org.id === orgId);
            if (activeOrg) {
              setCurrentOrganization(activeOrg);
              return;
            }
          }

          // Fallback: if no organization found in JWT or it doesn't match our list,
          // set the first one as active
          console.log(
            "No matching organization found in JWT, using first organization",
          );
          setCurrentOrganization(organizations[0]);

          // And update the JWT by switching to this organization
          await switchOrganization(organizations[0].id);
        } catch (error) {
          console.error("Error setting organization from JWT:", error);
          // Fallback to first organization
          setCurrentOrganization(organizations[0]);
        }
      };

      setActiveOrgFromJwt();
    }
  }, [currentOrganization, organizations]);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const user = await getCurrentUser(supabase);
      if (!user) {
        console.error(
          "Failed to get authenticated user (timeout or not logged in)",
        );
        return;
      }

      console.log("Fetching organizations for user:", user.id);

      // Get the active organization ID from JWT
      const activeOrgId = await getOrganizationId();

      try {
        // Get organizations with membership info in a single query to ensure we only get user's organizations
        const { data, error } = await supabase
          .from("organization_members")
          .select(`
            organization_id,
            role,
            organizations!inner(id, name)
          `)
          .eq("user_id", user.id);

        if (error) {
          console.error("Error fetching organizations:", error);
          throw error;
        }

        // Transform the data to the expected format
        // organizations!inner is many-to-one: an object at runtime, typed as an array
        const orgs = data.map((item) => {
          const org = item.organizations as any;
          return { id: org.id, name: org.name, role: item.role };
        });

        setOrganizations(orgs);
      } catch (membershipError) {
        console.log("Using fallback to get organization");

        // Fallback: Try to get the active organization directly
        if (activeOrgId) {
          const { data: orgData, error: orgError } = await supabase
            .from("organizations")
            .select("id, name")
            .eq("id", activeOrgId);

          if (orgError) {
            console.error("Error in fallback organization fetch:", orgError);
            throw orgError;
          }

          const orgs =
            orgData?.map((org) => ({
              id: org.id,
              name: org.name,
              role: "member",
            })) || [];

          console.log("Organizations fetched via fallback:", orgs);
          setOrganizations(orgs);
        }
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrganizationSelect = async (organizationId: string) => {
    const selected = organizations.find((org) => org.id === organizationId);
    if (selected) {
      // Update local state immediately for better UX
      setCurrentOrganization(selected);
      setOpen(false);

      try {
        console.log("Switching to organization:", selected.id);

        // Use the switchOrganization helper from supabase.ts
        const { success, error } = await switchOrganization(selected.id);

        if (!success) {
          console.error("Failed to switch organization:", error);
          return;
        }

        // Verify that the JWT now contains the correct organization ID
        const newOrgId = await getOrganizationId();
        console.log("Verified organization ID in JWT:", newOrgId);

        if (newOrgId !== selected.id) {
          console.warn(
            "Organization ID in JWT doesn't match selected organization",
          );
        }

        // If everything looks good, we'll reload organizations to reflect changes
        await fetchOrganizations();

        // To fully apply organization change across application, trigger refetch of data
        if (loadOrganizations) {
          await loadOrganizations();
        }
      } catch (error) {
        console.error("Error updating current organization:", error);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background text-foreground border-border hover:bg-accent hover:text-accent-foreground"
          disabled={isLoading}
        >
          {isLoading
            ? "Loading..."
            : currentOrganization?.name || "Select organization"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0 bg-popover text-popover-foreground border-border rounded-md">
        <Command>
          <CommandInput placeholder="Search organization..." />
          <CommandList>
            <CommandEmpty>No organization found.</CommandEmpty>
            <CommandGroup>
              {organizations.map((org) => (
                <CommandItem
                  key={org.id}
                  value={org.id}
                  onSelect={() => handleOrganizationSelect(org.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentOrganization?.id === org.id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <span>{org.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {org.role}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {isPlatformAdmin && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem>
                    <CreateOrganizationButton
                      variant="ghost"
                      className="w-full justify-start p-0 font-normal"
                      onOrganizationCreated={fetchOrganizations}
                    />
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
